import { Suspense, useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";

const ASPECT = 1206 / 192;
const N = 96;
const GRAPHITE = new THREE.Color("#ddd6c8");
const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
const lerp = THREE.MathUtils.lerp;
const clamp = THREE.MathUtils.clamp;

// Procedural pencil-stroke texture: white stroke with feathered ends,
// soft edges and graphite grain — stretched along each bar it reads as a
// hand-drawn pencil line with tapered ends.
function makePencilTexture() {
  if (typeof document === "undefined") return null;
  const W = 256;
  const H = 24;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(W, H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const u = x / (W - 1);
      const v = y / (H - 1);
      const endFade = Math.min(1, Math.min(u, 1 - u) / 0.1);
      const edge = 1 - Math.abs(v - 0.5) * 2;
      const vFade = Math.pow(Math.max(0, edge), 0.65);
      let a = endFade * vFade * (0.68 + Math.random() * 0.32);
      if (Math.random() < 0.05) a *= 0.35; // graphite skips
      const idx = (y * W + x) * 4;
      img.data[idx] = 255;
      img.data[idx + 1] = 255;
      img.data[idx + 2] = 255;
      img.data[idx + 3] = Math.max(0, Math.min(255, a * 255));
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

// A loose, hand-drawn PERSPECTIVE sketch of a modern villa (20 strokes).
// Faint construction/guide lines are drawn first, then the massing on top —
// each stroke overshoots its corners and is slightly jittered for a pencil feel.
// Points are hand-placed 2-point-perspective projections. `g` = guide line.
const HOUSE_SEGS = [
  // --- construction / perspective guide lines (faint) ---
  { a: [-2.2, -1.2], b: [2.6, -0.55], g: true },
  { a: [-2.0, -1.42], b: [2.4, -0.9], g: true },
  { a: [-1.8, 1.0], b: [-1.8, 1.98], g: true }, // front-left corner line up
  { a: [1.5, 1.5], b: [2.75, 2.0], g: true }, // roof fan up-right
  { a: [-0.4, 1.5], b: [-0.98, 2.02], g: true }, // roof fan up-left
  { a: [0.1, -1.1], b: [3.05, -0.32], g: true }, // perspective line to right VP
  // --- foreground paving grid (perspective slabs) ---
  { a: [-2.9, -1.72], b: [3.0, -1.42], p: true },
  { a: [-2.5, -1.56], b: [2.7, -1.3], p: true },
  { a: [-2.1, -1.42], b: [2.3, -1.19], p: true },
  { a: [-2.6, -1.68], b: [-1.8, -1.22], p: true },
  { a: [-1.5, -1.7], b: [-1.0, -1.22], p: true },
  { a: [-0.5, -1.72], b: [-0.32, -1.22], p: true },
  { a: [0.55, -1.72], b: [0.4, -1.22], p: true },
  { a: [1.6, -1.68], b: [1.15, -1.22], p: true },
  { a: [2.6, -1.6], b: [1.85, -1.22], p: true },
  // --- slim tree / entourage (left) ---
  { a: [-2.72, -1.16], b: [-2.64, -0.18], d: true },
  { a: [-2.66, -0.55], b: [-2.98, -0.82], d: true },
  { a: [-2.64, -0.44], b: [-2.34, -0.74], d: true },
  // --- shrub / planting (left) ---
  { a: [-2.3, -1.16], b: [-2.16, -0.9], d: true },
  { a: [-2.16, -1.16], b: [-2.02, -0.88], d: true },
  { a: [-2.02, -1.16], b: [-1.9, -0.92], d: true },
  // --- main volume massing (2-point perspective) ---
  { a: [-1.8, -1.1], b: [0.1, -1.1] }, // front bottom
  { a: [0.1, -1.1], b: [0.1, 1.0] }, // front right vertical
  { a: [0.1, 1.0], b: [-1.8, 1.0] }, // front top
  { a: [-1.8, 1.0], b: [-1.8, -1.1] }, // front left vertical
  { a: [-1.8, 1.0], b: [-0.4, 1.5] }, // top D-D2
  { a: [0.1, 1.0], b: [1.5, 1.5] }, // top C-C2
  { a: [-0.4, 1.5], b: [1.5, 1.5] }, // back ridge
  { a: [0.1, -1.1], b: [1.5, -0.6] }, // side B-B2
  { a: [1.5, -0.6], b: [1.5, 1.5] }, // back vertical
  // --- lower wing massing ---
  { a: [0.1, -1.3], b: [1.9, -1.3] },
  { a: [1.9, -1.3], b: [1.9, -0.1] },
  { a: [1.9, -0.1], b: [0.1, -0.1] },
  { a: [1.9, -1.3], b: [2.6, -1.02] },
  { a: [1.9, -0.1], b: [2.6, 0.18] },
  { a: [2.6, -1.02], b: [2.6, 0.18] },
  // --- flat roof: slab thickness (fascia) + raised parapet wall + chimney ---
  // slab edge / fascia — a thin band under the top edges gives the roof real depth
  { a: [-1.82, 0.9], b: [0.12, 0.9], d: true }, // front fascia underline
  { a: [-1.8, 1.0], b: [-1.82, 0.9], d: true }, // front-left slab return
  { a: [0.1, 1.0], b: [0.12, 0.9], d: true }, // front-right slab return
  { a: [0.16, 0.92], b: [1.5, 1.42], d: true }, // right fascia underline
  { a: [1.5, 1.5], b: [1.5, 1.42], d: true }, // right slab return
  // raised parapet wall around the whole roof perimeter (outer top edge)
  { a: [-1.8, 1.18], b: [0.1, 1.18] }, // parapet front top
  { a: [-1.8, 1.18], b: [-0.4, 1.68] }, // parapet left top
  { a: [0.1, 1.18], b: [1.5, 1.68] }, // parapet right top
  { a: [-0.4, 1.68], b: [1.5, 1.68] }, // parapet back top
  // parapet corner posts (roof corner up to parapet top)
  { a: [-1.8, 1.0], b: [-1.8, 1.18], d: true },
  { a: [0.1, 1.0], b: [0.1, 1.18], d: true },
  { a: [-0.4, 1.5], b: [-0.4, 1.68], d: true },
  { a: [1.5, 1.5], b: [1.5, 1.68], d: true },
  // parapet inner coping lip — faint, reads as wall thickness on the visible faces
  { a: [-1.72, 1.11], b: [0.02, 1.11], d: true }, // inner front
  { a: [0.02, 1.11], b: [1.4, 1.57], d: true }, // inner right
  // chimney — a slim rendered box standing above the parapet (back-left of roof)
  { a: [-1.28, 1.32], b: [-1.28, 1.86], d: true }, // front-left vertical
  { a: [-1.02, 1.36], b: [-1.02, 1.9], d: true }, // front-right vertical
  { a: [-1.28, 1.32], b: [-1.02, 1.36], d: true }, // base front
  { a: [-1.28, 1.86], b: [-1.02, 1.9], d: true }, // cap front
  { a: [-1.02, 1.9], b: [-0.86, 1.97], d: true }, // cap receding
  { a: [-0.86, 1.43], b: [-0.86, 1.97], d: true }, // back-right vertical
  { a: [-1.02, 1.36], b: [-0.86, 1.43], d: true }, // base receding
  // --- front facade: open ground floor (pilotis) + glazed upper ---
  { a: [-1.8, -0.1], b: [0.1, -0.1], d: true }, // floor slab line
  { a: [-1.35, -1.1], b: [-1.35, -0.1], d: true }, // column 1
  { a: [-0.72, -1.1], b: [-0.72, -0.1], d: true }, // column 2
  { a: [-1.35, -0.1], b: [-1.35, 1.0], d: true }, // upper mullion 1
  { a: [-0.72, -0.1], b: [-0.72, 1.0], d: true }, // upper mullion 2
  { a: [-1.8, 0.45], b: [0.1, 0.45], d: true }, // upper transom
  { a: [-1.8, 0.0], b: [0.1, 0.0], d: true }, // storey-division transom
  { a: [-1.15, -0.1], b: [-1.15, 1.0], d: true }, // fine mullion
  { a: [-0.28, -0.1], b: [-0.28, 1.0], d: true }, // fine mullion
  // --- side (receding) facade glazing ---
  { a: [0.1, -0.05], b: [1.5, 0.45], d: true }, // lower transom
  { a: [0.1, 0.5], b: [1.5, 1.0], d: true }, // upper transom
  { a: [0.65, -0.78], b: [0.65, 1.2], d: true }, // mullion
  { a: [1.05, -0.62], b: [1.05, 1.28], d: true }, // mullion
  // --- lower wing glazing ---
  { a: [0.55, -1.3], b: [0.55, -0.1], d: true },
  { a: [1.05, -1.3], b: [1.05, -0.1], d: true },
  { a: [1.55, -1.3], b: [1.55, -0.1], d: true },
  { a: [0.1, -0.72], b: [1.9, -0.72], d: true },
  // --- entrance (right of wing) + canopy + steps ---
  { a: [2.15, -1.3], b: [2.15, -0.55], d: true },
  { a: [2.15, -0.55], b: [2.6, -0.4], d: true },
  { a: [2.1, -0.52], b: [2.78, -0.3], d: true }, // entrance canopy
  { a: [2.05, -1.44], b: [2.55, -1.44], p: true }, // step 1
  { a: [2.15, -1.58], b: [2.62, -1.58], p: true }, // step 2
  // --- shadow hatching (short parallel strokes) ---
  { a: [2.05, -1.3], b: [1.65, -1.62], h: true },
  { a: [2.35, -1.22], b: [1.95, -1.54], h: true },
  { a: [2.62, 0.05], b: [2.28, -0.24], h: true },
  { a: [2.6, -0.3], b: [2.26, -0.58], h: true },
  { a: [-1.55, -1.15], b: [-1.85, -1.46], h: true },
  { a: [-1.05, -1.15], b: [-1.35, -1.46], h: true },
  { a: [-0.5, -1.16], b: [-0.8, -1.46], h: true },
];

// morph timeline (seconds)
const T_FADE = 0.35; // logo fades out
const DRAW_STEP = 0.07; // stagger between strokes (slower, more deliberate)
const DRAW_DUR = 0.5; // per-stroke draw time
const HOLD = 2.2; // hold the finished house
const T_ERASE = 0.5; // house fades out
const T_IN = 0.6; // logo fades back in

function LogoAssembly() {
  const group = useRef();
  const meshRefs = useRef([]);
  const base = useTexture("/logo_light.png");
  base.colorSpace = THREE.SRGBColorSpace;
  base.anisotropy = 8;

  const allowedRef = useRef(false);
  const startRef = useRef(null);
  const assembledRef = useRef(false);
  const clockRef = useRef(0);
  const morphActive = useRef(false);
  const morphStart = useRef(0);
  const solidNow = useRef(false);
  const swayRef = useRef(0);
  const shownRef = useRef(false);
  const hiddenRef = useRef(false);
  const drag = useRef({ active: false, rotX: 0, rotY: 0, moved: 0 });
  const coarse =
    typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  const w = 7.4;
  const h = w / ASPECT;
  const pw = w / N;
  const pencilTex = useMemo(() => makePencilTexture(), []);

  const strips = useMemo(() => {
    const arr = [];
    for (let i = 0; i < N; i++) {
      const tex = base.clone();
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.repeat.set(1 / N, 1);
      tex.offset.set(i / N, 0);
      tex.needsUpdate = true;
      const centerDist = Math.abs(i - (N - 1) / 2) / ((N - 1) / 2);
      arr.push({
        tex,
        x: -w / 2 + (i + 0.5) * (w / N),
        fromY: (i % 2 ? 0.9 : -0.9) + (Math.random() - 0.5) * 0.9,
        fromZ: -1.6 - Math.random() * 2.0,
        fromRot: (Math.random() - 0.5) * 1.6,
        delay: 0.15 + centerDist * 0.6 + Math.random() * 0.08,
      });
    }
    return arr;
  }, [base]);

  // Precompute a sketchy bar transform for each stroke: overshoot past the
  // corners + slight hand-drawn jitter, thinner/fainter for guide lines.
  const house = useMemo(() => {
    const rnd = (m) => (Math.random() - 0.5) * 2 * m;
    return HOUSE_SEGS.map((sg) => {
      const dx = sg.b[0] - sg.a[0];
      const dy = sg.b[1] - sg.a[1];
      const baseLen = Math.hypot(dx, dy) || 0.0001;
      const ux = dx / baseLen;
      const uy = dy / baseLen;
      let over, BT, op;
      if (sg.g) {
        over = 0.06 + Math.random() * 0.1;
        BT = 0.036;
        op = 0.24;
      } else if (sg.h) {
        over = 0.02 + Math.random() * 0.04;
        BT = 0.034;
        op = 0.26;
      } else if (sg.p) {
        over = 0.02 + Math.random() * 0.05;
        BT = 0.03;
        op = 0.36;
      } else if (sg.d) {
        over = 0.015 + Math.random() * 0.05;
        BT = 0.05 + Math.random() * 0.016;
        op = 0.74 + Math.random() * 0.12;
      } else {
        over = 0.025 + Math.random() * 0.075;
        BT = 0.07 + Math.random() * 0.03;
        op = 0.9 + Math.random() * 0.1;
      }
      const L = baseLen + over * 2;
      const mx = (sg.a[0] + sg.b[0]) / 2;
      const my = (sg.a[1] + sg.b[1]) / 2;
      const z = sg.g || sg.h || sg.p ? -0.12 : clamp(-(my * 0.16 + mx * 0.045), -0.32, 0.24);
      return {
        x: mx + rnd(0.018),
        y: my + rnd(0.018),
        z,
        rot: Math.atan2(dy, dx) + rnd(0.016),
        sx: L / pw,
        sy: BT / h,
        len: L,
        dx: ux,
        dy: uy,
        op,
      };
    });
  }, [pw, h]);

  const segCount = house.length;

  useEffect(() => {
    const onIntro = () => {
      allowedRef.current = true;
    };
    window.addEventListener("ag:intro-done", onIntro);
    const fb = setTimeout(() => {
      allowedRef.current = true;
    }, 4000);
    return () => {
      window.removeEventListener("ag:intro-done", onIntro);
      clearTimeout(fb);
    };
  }, []);

  const onDown = (e) => {
    e.stopPropagation();
    drag.current.active = true;
    drag.current.moved = 0;
  };
  const onMove = (e) => {
    if (!drag.current.active || coarse) return;
    const dx = e.nativeEvent.movementX || 0;
    const dy = e.nativeEvent.movementY || 0;
    drag.current.rotY += dx * 0.01;
    drag.current.rotX = THREE.MathUtils.clamp(drag.current.rotX + dy * 0.01, -0.8, 0.8);
    drag.current.moved += Math.abs(dx) + Math.abs(dy);
  };
  const onUp = () => {
    drag.current.active = false;
  };
  const onDouble = (e) => {
    e.stopPropagation();
    if (!allowedRef.current || morphActive.current) return;
    morphActive.current = true;
    morphStart.current = clockRef.current;
    shownRef.current = false;
    hiddenRef.current = false;
    window.dispatchEvent(new Event("ag:logo-morph"));
  };

  const tA = T_FADE;
  const tB = tA + (segCount - 1) * DRAW_STEP + DRAW_DUR;
  const tC = tB + HOLD;
  const tD = tC + T_ERASE;
  const tE = tD + T_IN;

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    clockRef.current = t;
    if (allowedRef.current && startRef.current === null) {
      startRef.current = t;
    }
    const started = startRef.current !== null;

    if (started && !assembledRef.current && t - startRef.current >= 0.9 + 2.4) {
      assembledRef.current = true;
      window.dispatchEvent(new Event("ag:logo-assembled"));
    }

    const active = morphActive.current;
    const lt = active ? t - morphStart.current : 0;

    if (active) {
      const wantSolid = lt >= tA && lt < tD;
      if (wantSolid !== solidNow.current) {
        solidNow.current = wantSolid;
        strips.forEach((s, i) => {
          const m = meshRefs.current[i];
          if (!m) return;
          if (wantSolid) {
            m.material.map = pencilTex;
            m.material.color.copy(GRAPHITE);
          } else {
            m.material.map = s.tex;
            m.material.color.setRGB(1, 1, 1);
          }
          m.material.needsUpdate = true;
        });
      }
      if (!shownRef.current && lt >= tB) {
        shownRef.current = true;
        window.dispatchEvent(new Event("ag:house-shown"));
      }
      if (!hiddenRef.current && lt >= tC) {
        hiddenRef.current = true;
        window.dispatchEvent(new Event("ag:house-hidden"));
      }
      if (lt >= tE) morphActive.current = false;
    }

    strips.forEach((s, i) => {
      const m = meshRefs.current[i];
      if (!m) return;
      // never render the wordmark strips until the assembly actually begins
      m.visible = started;
      let e = 0;
      if (started) {
        const p = clamp((t - startRef.current - s.delay) / 2.4, 0, 1);
        e = easeOutCubic(p);
      }
      const logoX = s.x;
      const logoY = lerp(s.fromY, 0, e);
      const logoZ = lerp(s.fromZ, 0, e);
      const logoRotX = lerp(s.fromRot, 0, e);

      if (!morphActive.current) {
        m.position.set(logoX, logoY, logoZ);
        m.rotation.set(logoRotX, 0, 0);
        m.scale.set(1, 1, 1);
        m.material.opacity = e;
        return;
      }

      if (lt < tA) {
        // logo fades out
        m.position.set(logoX, logoY, logoZ);
        m.rotation.set(logoRotX, 0, 0);
        m.scale.set(1, 1, 1);
        m.material.opacity = e * (1 - lt / tA);
      } else if (lt < tD && i < segCount) {
        // house: draw stroke-by-stroke, hold, then erase
        const ht = house[i];
        let dp;
        if (lt < tB) dp = clamp((lt - tA - i * DRAW_STEP) / DRAW_DUR, 0, 1);
        else dp = 1;
        const grow = easeOutCubic(dp);
        const half = (ht.len / 2) * (1 - grow);
        m.position.x = ht.x - ht.dx * half;
        m.position.y = ht.y - ht.dy * half;
        m.position.z = ht.z;
        m.rotation.set(0, 0, ht.rot);
        m.scale.set(ht.sx * Math.max(grow, 0.001), ht.sy, 1);
        let hop;
        if (lt < tB) hop = grow;
        else if (lt < tC) hop = 1;
        else hop = 1 - (lt - tC) / T_ERASE;
        m.material.opacity = Math.max(0, hop) * ht.op;
      } else if (lt >= tD) {
        // logo fades back in
        m.position.set(logoX, logoY, logoZ);
        m.rotation.set(logoRotX, 0, 0);
        m.scale.set(1, 1, 1);
        m.material.opacity = clamp((lt - tD) / T_IN, 0, 1) * e;
      } else {
        // strips beyond the stroke count stay hidden during the house
        m.material.opacity = 0;
      }
    });

    if (group.current) {
      const px = state.pointer.x;
      const py = state.pointer.y;
      const floatY = started ? Math.sin(t * 0.8) * 0.05 : 0;
      group.current.position.y = lerp(group.current.position.y, floatY, 0.1);
      const fit = Math.min(1, (state.viewport.width * 0.9) / w);
      group.current.scale.setScalar(fit);
      if (drag.current.active && !coarse) {
        group.current.rotation.y = drag.current.rotY;
        group.current.rotation.x = drag.current.rotX;
      } else {
        const houseVis = morphActive.current && lt >= tA && lt < tD ? 1 : 0;
        swayRef.current = lerp(swayRef.current, houseVis, 0.05);
        const sway = swayRef.current;
        const targetY =
          drag.current.rotY +
          px * 0.35 +
          Math.sin(t * 0.3) * 0.04 +
          sway * Math.sin(t * 0.5) * 0.16;
        const targetX =
          drag.current.rotX + -py * 0.22 + sway * (0.05 + Math.sin(t * 0.42 + 1) * 0.05);
        group.current.rotation.y = lerp(group.current.rotation.y, targetY, 0.05);
        group.current.rotation.x = lerp(group.current.rotation.x, targetX, 0.05);
      }
    }
  });

  return (
    <group ref={group}>
      {/* invisible hit area for drag / double-click */}
      <mesh
        position={[0, 0, 0.25]}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerOut={onUp}
        onDoubleClick={onDouble}
      >
        <planeGeometry args={[w * 1.2, h * 4.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {strips.map((s, i) => (
        <mesh key={i} ref={(el) => (meshRefs.current[i] = el)}>
          <planeGeometry args={[w / N, h]} />
          <meshBasicMaterial
            map={s.tex}
            transparent
            opacity={0}
            toneMapped={false}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
}

export default function Logo3D() {
  return (
    <Canvas
      camera={{ position: [0, 0, 6], fov: 45 }}
      dpr={[1, 2]}
      gl={{ alpha: true, antialias: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={1} />
      <Suspense fallback={null}>
        <LogoAssembly />
      </Suspense>
    </Canvas>
  );
}
